import { toSvg } from "html-to-image";
import { uniq, isEqual, uniqBy } from "lodash";
import { MetricTypes } from "../global/global.types";
import { PsCalculationResultsDataItemState, EngineResultType, GraphItems, } from "./ps-workspace.types";
import { ResultSetElementType } from "./ps-workspace.update.types";
import { renderTabularViewValue } from "utils/ps-workspaceHelpers";
import { convertToCSV, formatNumberForExport } from "utils/string";
import { compareResultItemIds } from "utils/data";
import { GLOBAL_GRAPH_STYLES } from "ps-components/Plot/utils";
import * as legendStyles from "ps-components/Plot/components/Legend/styles.module.scss";
import * as resultInfoMessageStyles from "components/ResultInfoMessage/styles.module.scss";
export const MAX_NAME_LENGTH = 128;
export const MAX_NOTES_LENGTH = 1000;
export const MAX_ENTITIES_COUNT = 6;
const prepareResultSets = (workspace) => (workspace?.resultSets || []).map((resultSet) => {
    const tableElementsQuantity = resultSet.resultElements.tableElements.length;
    const lineGraphElementsQuantity = resultSet.resultElements.lineGraphElements.length;
    const heatMapElementsQuantity = resultSet.resultElements.heatMapElements.length;
    const tableElements = resultSet.resultElements.tableElements.map((tableElement, index) => {
        const configurations = [];
        (tableElement.configurations || []).forEach(({ analysisId, configurationId, id }) => {
            const analysis = workspace?.analyses.find(({ id }) => id === analysisId) || {};
            const { name: configuration } = (analysis.configurations || []).find(({ id }) => id === configurationId) || {};
            const analysisName = analysis.name || `Scenario #${analysisId}`;
            configurations.push({
                id,
                analysisId,
                configurationId,
                analysis: analysisName,
                configuration: configuration ?? "",
                composedName: configuration,
            });
        });
        return { ...tableElement, configurations, index: index + 1, type: EngineResultType.Result0D };
    });
    const lineGraphElements = resultSet.resultElements.lineGraphElements.map((lineGraphElement, index) => ({
        ...lineGraphElement,
        index: tableElementsQuantity + index + 1,
        type: EngineResultType.Result1D,
    }));
    const heatMapElements = resultSet.resultElements.heatMapElements.map((lineGraphElement, index) => ({
        ...lineGraphElement,
        index: tableElementsQuantity + lineGraphElementsQuantity + index + 1,
        type: EngineResultType.Result2D,
    }));
    const metadataElements = resultSet.resultElements.metadataElements.map((metadataElement, index) => {
        const configurations = [];
        (metadataElement.configurations || []).forEach(({ analysisId, configurationId, id }) => {
            const analysis = workspace?.analyses.find(({ id }) => id === analysisId) || {};
            const { name: configuration } = (analysis.configurations || []).find(({ id }) => id === configurationId) || {};
            const analysisName = analysis.name || `Scenario #${analysisId}`;
            configurations.push({
                id,
                analysisId,
                configurationId,
                analysis: analysisName,
                configuration: configuration ?? "",
                composedName: configuration,
            });
        });
        return {
            ...metadataElement,
            configurations,
            index: tableElementsQuantity + lineGraphElementsQuantity + heatMapElementsQuantity + index + 1,
            type: EngineResultType.ResultMetadata,
        };
    });
    return {
        ...resultSet,
        resultElements: {
            ...resultSet.resultElements,
            tableElements,
            lineGraphElements,
            heatMapElements,
            metadataElements,
        },
    };
});
const prepareScenarios = (workspace) => (workspace.analyses || []).map((scenario) => {
    const configurationsById = scenario.configurations.reduce((acc, configuration) => ({ ...acc, [configuration.id]: configuration }), {});
    const absoluteRiskProfilesById = scenario.absoluteRiskProfiles.reduce((acc, profile) => ({ ...acc, [profile.id]: profile }), {});
    const relativeRiskProfilesById = scenario.relativeRiskProfiles.reduce((acc, profile) => ({ ...acc, [profile.id]: profile }), {});
    const absoluteRiskProfiles = prepareRiskProfiles(scenario, configurationsById, MetricTypes.simple);
    const relativeRiskProfiles = prepareRiskProfiles(scenario, configurationsById, MetricTypes.relative);
    const configurations = scenario.configurations.map((configuration) => {
        const rawAttachedRelativeRisks = scenario.attachedRelativeRisks.reduce((acc, risk) => {
            if (risk.sourceId === null ||
                configuration.id === risk.sourceId ||
                risk.targetId === null ||
                configuration.id === risk.targetId) {
                const attachedConfiguration = {
                    ...risk,
                    source: risk.sourceId === null ? "" : configurationsById[risk.sourceId]?.name,
                    target: risk.targetId === null ? "" : configurationsById[risk.targetId]?.name,
                };
                if (acc[risk.riskId]) {
                    acc[risk.riskId].configurations.push(attachedConfiguration);
                }
                else {
                    acc[risk.riskId] = {
                        riskId: risk.riskId,
                        name: relativeRiskProfilesById[risk.riskId].name,
                        paramId: relativeRiskProfilesById[risk.riskId].paramId,
                        templateId: relativeRiskProfilesById[risk.riskId].templateId,
                        configurations: [attachedConfiguration],
                    };
                }
            }
            return acc;
        }, {});
        const rawAttachedAbsoluteRisks = scenario.attachedAbsoluteRisks.reduce((acc, risk) => {
            if (configuration.id === risk.targetId || risk.targetId === null) {
                const attachedConfiguration = {
                    ...risk,
                    target: risk.targetId === null ? "" : configurationsById[risk.targetId].name,
                };
                if (acc[risk.riskId]) {
                    acc[risk.riskId].configurations.push(attachedConfiguration);
                }
                else {
                    acc[risk.riskId] = {
                        riskId: risk.riskId,
                        name: absoluteRiskProfilesById[risk.riskId].name,
                        paramId: absoluteRiskProfilesById[risk.riskId].paramId,
                        templateId: absoluteRiskProfilesById[risk.riskId].templateId,
                        configurations: [attachedConfiguration],
                    };
                }
            }
            return acc;
        }, {});
        const attachedAbsoluteRisks = Object.values(rawAttachedAbsoluteRisks);
        const attachedRelativeRisks = Object.values(rawAttachedRelativeRisks);
        return { ...configuration, attachedAbsoluteRisks, attachedRelativeRisks };
    });
    return {
        ...scenario,
        configurations,
        absoluteRiskProfiles,
        relativeRiskProfiles,
    };
});
const prepareRiskProfiles = (scenario, configurations, type) => type === "relative"
    ? scenario.relativeRiskProfiles.map((profile) => {
        const filteredAttachments = scenario.attachedRelativeRisks
            .filter(({ riskId }) => riskId === profile.id)
            .map((attachment) => ({
            ...attachment,
            source: attachment.sourceId === null ? "" : configurations[attachment.sourceId]?.name,
            target: attachment.targetId === null ? "" : configurations[attachment.targetId]?.name,
        }));
        return { ...profile, configurations: filteredAttachments };
    })
    : scenario.absoluteRiskProfiles.map((profile) => {
        const filteredAttachments = scenario.attachedAbsoluteRisks
            .filter(({ riskId }) => riskId === profile.id)
            .map((attachment) => ({
            ...attachment,
            target: attachment.targetId === null ? "" : configurations[attachment.targetId]?.name,
        }));
        return { ...profile, configurations: filteredAttachments };
    });
export const getResultSetElementsCount = (resultSet) => {
    if (!resultSet.resultElements) {
        return 0;
    }
    const resultElements = resultSet.resultElements;
    const elements = [
        ...resultElements.tableElements,
        ...resultElements.lineGraphElements,
        ...resultElements.heatMapElements,
        ...resultElements.metadataElements,
    ];
    return elements.length;
};
export const prepareWorkspace = (workspace) => {
    const resultSets = prepareResultSets(workspace);
    const analyses = prepareScenarios(workspace);
    return { ...workspace, analyses, resultSets };
};
const placeTableElements = (element, existingResultSetLayout) => {
    const existingTableElementLayout = existingResultSetLayout?.tableElements.find((item) => parseInt(item.id) === element.id);
    return {
        id: element.id.toString(),
        configurations: element.configurations.map((configuration, index) => {
            const existingOrder = existingTableElementLayout?.configurations.find((item) => item.analysisId === configuration.analysisId &&
                item.configurationId === configuration.configurationId)?.order;
            return {
                analysisId: configuration.analysisId,
                configurationId: configuration.configurationId,
                order: existingOrder || index.toString(),
            };
        }),
        parameters: element.parameters.map((parameter, index) => {
            const existingOrder = existingTableElementLayout?.parameters.find((item) => item.parameterId === parameter.parameterId && item.unitsId === parameter.unitsId)?.order;
            return {
                parameterId: parameter.parameterId,
                unitsId: parameter.unitsId,
                order: existingOrder || index.toString(),
            };
        }),
    };
};
const placeMetadataElements = (element, existingResultSetLayout) => {
    const existingMetadataElementLayout = (existingResultSetLayout?.metadataElements || []).find((item) => parseInt(item.id) === element.id);
    return {
        id: element.id.toString(),
        configurations: element.configurations.map((configuration, index) => {
            const existingOrder = existingMetadataElementLayout?.configurations.find((item) => item.analysisId === configuration.analysisId &&
                item.configurationId === configuration.configurationId)?.order;
            return {
                analysisId: configuration.analysisId,
                configurationId: configuration.configurationId,
                order: existingOrder || index.toString(),
            };
        }),
    };
};
export const prepareWorkspaceLayout = (workspace, existingLayout, duplicatedResultSetId) => {
    const TEMP_TYPES_MAP = {
        TabularView: 1,
        LineGraph: 2,
        HeatMap: 3,
        MetaData: 4,
    };
    const workspaceLayout = workspace.resultSets.map((resultSet) => {
        let preparedResultSetElements = [];
        let newPreparedItem;
        let allElementsCounter = 0;
        let leftElementsCounter = 0;
        let rightElementsCounter = 0;
        const allResultSetElements = [
            ...resultSet.resultElements.tableElements.map((item) => ({
                ...item,
                type: ResultSetElementType.TabularView,
            })),
            ...resultSet.resultElements.lineGraphElements.map((item) => ({
                ...item,
                type: ResultSetElementType.LineGraph,
            })),
            ...resultSet.resultElements.heatMapElements.map((item) => ({
                ...item,
                type: ResultSetElementType.HeatMap,
            })),
            ...resultSet.resultElements.metadataElements.map((item) => ({
                ...item,
                type: ResultSetElementType.Metadata,
            })),
        ];
        const lastLeftElementIndex = Math.ceil(allResultSetElements.length / 2) - 1;
        const existingResultSetLayout = existingLayout?.find((item) => item.id === resultSet.id);
        const itemDeleted = allResultSetElements.length !== 0 &&
            existingResultSetLayout?.resultSetElements.length &&
            allResultSetElements.length < existingResultSetLayout?.resultSetElements.length;
        const leftExistingItems = [];
        const rightExistingItems = [];
        const allElementsAreNew = existingResultSetLayout?.resultSetElements === undefined ||
            (allResultSetElements.length !== 0 && existingResultSetLayout?.resultSetElements.length === 0);
        allResultSetElements.slice().forEach((element) => {
            const existingLayoutElement = existingResultSetLayout?.resultSetElements.find((item) => {
                const computedType = typeof item.type === "number" ? item.type : TEMP_TYPES_MAP[item.type];
                return item.elementIndex === element.id && computedType === element.type;
            });
            if (existingLayoutElement?.order !== undefined) {
                const [side, order] = existingLayoutElement?.order.split(".");
                if (side === "left") {
                    leftExistingItems.push({
                        id: element.id,
                        type: element.type,
                        isNew: false,
                        internalOrder: parseInt(order),
                    });
                }
                else {
                    rightExistingItems.push({
                        id: element.id,
                        type: element.type,
                        isNew: false,
                        internalOrder: parseInt(order),
                    });
                }
            }
            const isNew = !allElementsAreNew && existingLayoutElement?.elementIndex === undefined;
            if (isNew) {
                newPreparedItem = {
                    id: element.id,
                    type: element.type,
                    isNew,
                };
            }
        });
        if (allElementsAreNew) {
            preparedResultSetElements = allResultSetElements.slice().map((item) => ({
                id: item.id,
                type: item.type,
                isNew: true,
            }));
        }
        else {
            preparedResultSetElements = [
                ...leftExistingItems.sort((a, b) => (a.internalOrder || 0) - (b.internalOrder || 0)),
                ...rightExistingItems.sort((a, b) => (a.internalOrder || 0) - (b.internalOrder || 0)),
            ];
        }
        if (newPreparedItem) {
            preparedResultSetElements.push(newPreparedItem);
        }
        const resultSetElementsLayout = [];
        const placeLayoutItems = (type, id, isNew) => {
            const base = allElementsCounter <= lastLeftElementIndex ? "left" : "right";
            const existingOrder = existingResultSetLayout?.resultSetElements.find((item) => {
                const computedType = typeof item.type === "number" ? item.type : TEMP_TYPES_MAP[item.type];
                // TODO: ask BE to send numbers instead string types, 'TabularView' -> 1 etc.
                return item.elementIndex === id && computedType === type;
            })?.order;
            const newOrder = `${base}.${base === "left" ? leftElementsCounter : rightElementsCounter}`;
            if (!isNew) {
                resultSetElementsLayout.push({
                    type,
                    order: !!newPreparedItem || itemDeleted ? newOrder : existingOrder || newOrder,
                    elementIndex: id, // TODO: ask BE to rename elementIndex to elementId as we use Id's now
                });
            }
            else {
                resultSetElementsLayout.push({
                    type,
                    order: newOrder,
                    elementIndex: id,
                });
            }
            base === "left" ? leftElementsCounter++ : rightElementsCounter++;
            allElementsCounter++;
        };
        preparedResultSetElements.forEach(({ id, type, isNew }) => {
            placeLayoutItems(type, id, isNew);
        });
        const tableElements = resultSet.resultElements.tableElements.map((element) => placeTableElements(element, existingResultSetLayout));
        const metadataElements = resultSet.resultElements.metadataElements.map((element) => placeMetadataElements(element, existingResultSetLayout));
        return {
            id: resultSet.id,
            resultSetElements: resultSetElementsLayout,
            tableElements,
            metadataElements,
        };
    });
    if (duplicatedResultSetId !== null) {
        const duplicatedResultSetLayout = existingLayout?.find((item) => item.id === duplicatedResultSetId);
        const newlyAddedDuplicatedResultSet = workspace.resultSets[workspace.resultSets.length - 1];
        if (!!duplicatedResultSetLayout && !!newlyAddedDuplicatedResultSet) {
            workspaceLayout.splice(workspaceLayout.length - 1, 1, {
                id: newlyAddedDuplicatedResultSet.id,
                resultSetElements: duplicatedResultSetLayout?.resultSetElements,
                tableElements: duplicatedResultSetLayout?.tableElements,
                metadataElements: duplicatedResultSetLayout?.metadataElements,
            });
        }
    }
    return workspaceLayout;
};
export const getTabularParametersFromResponse = (results, parameterOrders) => results.reduce((acc, { parameters }) => {
    if (acc.length >= parameters.length) {
        return acc;
    }
    return parameters.map(({ parameter: { id: parameterId }, units: { id: unitsId } }) => {
        const orderEntity = parameterOrders.find((item) => item.parameterId === parameterId && item.unitsId === unitsId);
        return {
            parameterId,
            unitsId,
            composedId: `${parameterId}-${unitsId}`,
            order: orderEntity?.order,
        };
    });
}, []);
export const transformCalculationTabularResults = (results) => results.reduce((acc, { data, recordId }) => ({
    ...acc,
    ...(recordId === null
        ? {}
        : {
            [recordId]: {
                data: data.reduce((dataAcc, dataItems = []) => ({
                    ...dataAcc,
                    [`${dataItems[0]?.parameterId}-${dataItems[0]?.unitsId}`]: dataItems[0],
                }), {}),
                id: recordId,
            },
        }),
}), {});
export const prepareCalculationTabularResultEntry = (entry) => {
    entry.parameters.forEach((parameter, index) => {
        if (entry.data[index]?.[0]) {
            entry.data[index][0].parameterId = parameter.parameter?.id;
            entry.data[index][0].unitsId = parameter.units?.id;
        }
    });
    return entry;
};
export const prepareGraphResultsState = (currentState, payload) => {
    const { resultSetElementId, resultSetId, type, ...graphItemData } = payload;
    const storedGraphElement = currentState.find((item) => compareResultItemIds(item, payload));
    const filteredGraphElements = currentState.filter((item) => compareResultItemIds(item, payload, true));
    let dataKey;
    switch (type) {
        case EngineResultType.Result1D:
            dataKey = GraphItems.lines;
            break;
        case EngineResultType.Result2D:
            dataKey = GraphItems.contours;
            break;
    }
    if (!dataKey)
        return currentState;
    if (!storedGraphElement) {
        const preparedElement = {
            resultSetElementId,
            resultSetId,
            type,
            [dataKey]: [graphItemData],
        };
        return [...filteredGraphElements, preparedElement];
    }
    else {
        if (!(dataKey in storedGraphElement))
            return currentState;
        const { responseId, data } = graphItemData;
        const isPending = data.some((i) => {
            if (typeof i === "object" && "state" in i) {
                return i.state === PsCalculationResultsDataItemState.Pending;
            }
            else {
                return i.some(({ state }) => state === PsCalculationResultsDataItemState.Pending);
            }
        });
        const storedGraphDataItem = storedGraphElement[dataKey].find(({ responseId: id }) => id === responseId);
        const filteredGraphDataItems = storedGraphElement[dataKey].filter(({ responseId: id }) => id !== responseId);
        if (!storedGraphDataItem || !isPending) {
            const preparedElement = {
                ...storedGraphElement,
                [dataKey]: [...filteredGraphDataItems, graphItemData],
            };
            return [...filteredGraphElements, preparedElement];
        }
        else {
            let data = [];
            switch (type) {
                case EngineResultType.Result1D:
                    data = storedGraphDataItem.data.map((el) => ({
                        ...el,
                        state: PsCalculationResultsDataItemState.Pending,
                    }));
                    break;
                case EngineResultType.Result2D:
                    data = storedGraphDataItem.data.map((el) => el.map((i) => ({ ...i, state: PsCalculationResultsDataItemState.Pending })));
                    break;
            }
            const preparedElement = {
                ...storedGraphElement,
                [dataKey]: [
                    ...filteredGraphDataItems,
                    {
                        ...storedGraphDataItem,
                        data,
                    },
                ],
            };
            return [...filteredGraphElements, preparedElement];
        }
    }
};
export const getYAxisName = (item, array) => {
    const arrWithUniqueResponse = uniqBy(array, "responseId");
    const existItemIndex = arrWithUniqueResponse.findIndex((i) => i.responseId === item.responseId);
    return `y${existItemIndex ? existItemIndex + 1 : ""}`;
};
export const prepareStateOnRemoveStoredGraphItem = (currentState, itemToRemove) => {
    const { type, responseId } = itemToRemove;
    const storedGraphElement = currentState.find((item) => compareResultItemIds(item, itemToRemove));
    const filteredState = currentState.filter((item) => compareResultItemIds(item, itemToRemove, true));
    let dataKey;
    switch (type) {
        case EngineResultType.Result1D:
            dataKey = GraphItems.lines;
            break;
        case EngineResultType.Result2D:
            dataKey = GraphItems.contours;
            break;
    }
    if (!dataKey || !storedGraphElement || !(dataKey in storedGraphElement))
        return currentState;
    const filteredGraphElementItems = storedGraphElement[dataKey].filter(({ responseId: id }) => id !== responseId);
    return [...filteredState, { ...storedGraphElement, [dataKey]: filteredGraphElementItems }];
};
export const prepareKnownValues = (knownValues, dictionary) => knownValues.map((knownValue) => ({
    ...knownValue,
    ...(knownValue.id !== null
        ? {
            unit: dictionary[knownValue.id].units.find(({ id }) => id === knownValue.units),
        }
        : {}),
}));
export const getAttachmentsAvailabilityForRelativeMetric = (scenario, id) => {
    const { sourceMapping, targetMapping } = scenario.configurations.reduce((acc, { id }) => ({
        ...acc,
        sourceMapping: { ...acc.sourceMapping, [id]: [] },
        targetMapping: { ...acc.targetMapping, [id]: [] },
    }), { sourceMapping: {}, targetMapping: {} });
    scenario.attachedRelativeRisks.forEach(({ sourceId, targetId, riskId }) => {
        if (riskId === id && sourceId !== null && targetId !== null) {
            sourceMapping[sourceId]?.push(targetId);
            targetMapping[targetId]?.push(sourceId);
        }
    });
    const availableSources = Object.keys(sourceMapping)
        .map((key) => Number(key))
        .filter((sourceId) => sourceMapping[sourceId].length < scenario.configurations.length - 1);
    const availableTargets = Object.keys(targetMapping)
        .map((key) => Number(key))
        .filter((targetId) => targetMapping[targetId].length < scenario.configurations.length - 1);
    return { sourceMapping, targetMapping, availableSources, availableTargets };
};
export const getGraphPrintContent = async (element) => {
    let elementToDisplay;
    const rootElement = element?.querySelector(`.${GLOBAL_GRAPH_STYLES.root}`)?.cloneNode(false);
    if (rootElement) {
        const header = element.querySelector(`.${GLOBAL_GRAPH_STYLES.header}`)?.cloneNode(true);
        if (header) {
            rootElement.appendChild(header);
        }
        const plotRootEl = element.querySelector(`.${GLOBAL_GRAPH_STYLES.plotRoot}`);
        const plotLegendRootEl = element.querySelector(`.${GLOBAL_GRAPH_STYLES.legendRoot}`);
        const disabledLegendRows = plotRootEl?.querySelectorAll(`.${GLOBAL_GRAPH_STYLES.hiddenRow}`);
        const disabledRowsLength = disabledLegendRows?.length ?? 0;
        if (plotRootEl) {
            const imageData = await toSvg(plotRootEl, {
                filter: (domNode) => {
                    if (!domNode.classList) {
                        return true;
                    }
                    else {
                        return !(domNode.classList.contains(`${resultInfoMessageStyles.root}`) ||
                            domNode.classList.contains(`${GLOBAL_GRAPH_STYLES.legendRoot}`));
                    }
                },
                height: plotRootEl.clientHeight - ((plotLegendRootEl?.clientHeight ?? 0) - disabledRowsLength * 32),
            });
            const imageContainer = document.createElement("div");
            imageContainer.setAttribute("style", "display: block; width: 100%;");
            const image = new Image();
            image.src = imageData;
            image.setAttribute("style", "width: 100%");
            imageContainer.appendChild(image);
            rootElement.appendChild(imageContainer);
        }
        if (plotLegendRootEl) {
            const actions = element.querySelector(`.${GLOBAL_GRAPH_STYLES.legendActions}`);
            const imageData = await toSvg(plotLegendRootEl, {
                filter: (domNode) => {
                    if (!domNode.classList) {
                        return true;
                    }
                    else {
                        return !(domNode.classList.contains(`${legendStyles["print-suppres"]}`) ||
                            domNode.classList.contains(`${legendStyles.actions}`) ||
                            domNode.classList.contains(`${GLOBAL_GRAPH_STYLES.hiddenRow}`));
                    }
                },
                height: plotLegendRootEl.clientHeight - (actions?.clientHeight ?? 0) - disabledRowsLength * 32,
            });
            const imageContainer = document.createElement("div");
            imageContainer.setAttribute("style", "display: block; width: 100%;");
            const image = new Image();
            image.src = imageData;
            image.setAttribute("style", "width: 100%");
            imageContainer.appendChild(image);
            rootElement.appendChild(imageContainer);
        }
        elementToDisplay = element.cloneNode(false).appendChild(rootElement);
    }
    else {
        elementToDisplay = element;
    }
    return elementToDisplay;
};
export const convertTabularResultsToCsv = (results, layout) => {
    const headers = ["Scenario", "Configuration"];
    const rows = [];
    const valueRenderers = {
        pending: () => ["Pending", "Pending"],
        noPossible: () => ["No possible value", "No possible value"],
        undefined: () => ["Undefined", "Undefined"],
        normal: (data) => [
            data.valuesWithConfidence.map(({ value }) => formatNumberForExport(value)),
            data.valuesWithConfidence.map(({ confidence }) => formatNumberForExport(confidence)),
        ],
    };
    const transformedResults = transformCalculationTabularResults(results);
    layout.forEach((layoutRow, index) => {
        if (layoutRow.id === null) {
            return;
        }
        let maxValuesCount = 0;
        const rowBeginning = [`"${layoutRow.analysis}"`, `"${layoutRow.rowName}"`];
        const row = [...rowBeginning];
        const recordResults = transformedResults[layoutRow.id];
        if (!recordResults) {
            return;
        }
        layoutRow.columns.forEach(({ name, composedId }) => {
            if (index === 0) {
                headers.push(`"${name}"`);
                headers.push(`"${name} Confidence"`);
            }
            maxValuesCount =
                recordResults.data?.[composedId].valuesWithConfidence.length > maxValuesCount
                    ? recordResults.data?.[composedId].valuesWithConfidence.length
                    : maxValuesCount;
            const [values, confidences] = renderTabularViewValue(recordResults.data?.[composedId], valueRenderers);
            row.push(Array.isArray(values) ? values[0] : values);
            row.push(Array.isArray(confidences) ? confidences[0] : confidences);
        });
        rows.push(row);
        if (maxValuesCount > 1) {
            for (let valuesIndex = 1; valuesIndex < maxValuesCount; valuesIndex++) {
                const additionalRow = [...rowBeginning];
                layoutRow.columns.forEach(({ composedId }) => {
                    const [values, confidences] = renderTabularViewValue(recordResults.data?.[composedId], valueRenderers);
                    additionalRow.push(Array.isArray(values) ? values[valuesIndex] : values);
                    additionalRow.push(Array.isArray(confidences) ? confidences[valuesIndex] : confidences);
                });
                rows.push(additionalRow);
            }
        }
    });
    return convertToCSV([headers, ...rows]);
};
export const convertLinegraphResultsToCsv = (results, data) => {
    const { xParameter, xUnit, responses } = data;
    const headers = [
        "x-scenario",
        "x-configuration",
        "y-configuration",
        `"x-parameter - ${xParameter} (${xUnit})"`,
    ];
    const rows = [];
    results.forEach(({ responseId }, index) => {
        const { yParameter, yUnits } = responses.find(({ id }) => id === responseId) || {};
        headers.push(`"y-parameter${index + 1} - ${yParameter} (${yUnits})"`);
    });
    const allXValues = uniq(results.reduce((acc, { x }) => acc.concat(x), [])).sort((a, b) => (a === null || b === null ? 0 : a - b));
    allXValues.forEach((xValue) => {
        const rowsData = [];
        results.forEach((result, index) => {
            const { analysis, analysisId, xConfiguration, xConfigurationId, yConfiguration, yConfigurationId, id } = responses.find(({ id }) => id === result.responseId) || {};
            const { y: yValues, x: xValues } = result;
            if (!yValues || !xValues) {
                return;
            }
            let yValue = "";
            if (result.isLoading) {
                yValue = "Pending";
            }
            else {
                const xValueIndex = xValues.findIndex((x) => xValue === x);
                if (xValueIndex !== -1) {
                    yValue = `"${yValues[xValueIndex]}"`;
                }
            }
            const existedRowsDataIndex = rowsData.findIndex((rowsDataItem) => rowsDataItem.analysisId === analysisId &&
                rowsDataItem.xConfigurationId === xConfigurationId &&
                rowsDataItem.yConfigurationId === yConfigurationId);
            if (existedRowsDataIndex !== -1) {
                rowsData[existedRowsDataIndex].y.push(yValue);
            }
            else {
                const rowData = {
                    responseId: id,
                    analysis,
                    analysisId,
                    xConfiguration,
                    xConfigurationId,
                    yConfiguration,
                    yConfigurationId,
                    x: `"${xValue}"`,
                    y: [...new Array(index).fill(""), yValue],
                };
                rowsData.push(rowData);
            }
        });
        const rowsForXValue = rowsData.map(({ analysis, xConfiguration, yConfiguration, x, y }) => [
            `"${analysis}"`,
            `"${xConfiguration}"`,
            `"${yConfiguration}"`,
            x,
            ...y,
        ]);
        rows.push(...rowsForXValue);
    });
    return convertToCSV([headers, ...rows]);
};
export const convertHeatmapResultsToCsv = (results, data) => {
    const { xParameter, xUnit, yParameter, yUnit, responses } = data;
    const headers = [
        "Scenario",
        "x-configuration",
        "y-configuration",
        "z-configuration",
        `"x-parameter - ${xParameter} (${xUnit})"`,
        `"y-parameter - ${yParameter} (${yUnit})"`,
    ];
    results.forEach(({ responseId }, index) => {
        const { zParameter, zUnit } = responses.find(({ id }) => id === responseId) || {};
        headers.push(`"z-parameter${index + 1} - ${zParameter} (${zUnit})"`);
    });
    const rows = [];
    const allXValues = uniq(results.reduce((acc, { x }) => acc.concat(x), [])).sort((a, b) => (a === null || b === null ? 0 : a - b));
    allXValues.forEach((xValue) => {
        const rowsData = [];
        results.forEach((contour, index) => {
            const { analysis, analysisId, xConfiguration, xConfigurationId, yConfiguration, yConfigurationId, zConfigurationId, zConfiguration, id, } = responses.find(({ id }) => id === contour.responseId) || {};
            const { x: xValues, y: yValues, z: zValues } = contour;
            if (!yValues || !xValues || !zValues) {
                return;
            }
            const xValueIndex = xValues.findIndex((x) => xValue === x);
            const rowDataTemplate = {
                responseId: id,
                analysis,
                analysisId,
                xConfiguration,
                xConfigurationId,
                yConfiguration,
                yConfigurationId,
                zConfiguration,
                zConfigurationId,
                x: xValue,
                y: null,
                z: new Array(results.length).fill(null),
            };
            if (contour.isLoading) {
                rowDataTemplate.pending = true;
            }
            if (xValueIndex !== -1) {
                yValues.forEach((yValue, yValueIndex) => {
                    const existedRowsDataIndex = index === 0
                        ? -1
                        : rowsData.findIndex((rowsDataItem) => rowsDataItem.analysisId === analysisId &&
                            rowsDataItem.xConfigurationId === xConfigurationId &&
                            rowsDataItem.yConfigurationId === yConfigurationId &&
                            rowsDataItem.zConfigurationId === zConfigurationId &&
                            rowsDataItem.x === xValue &&
                            rowsDataItem.y === yValue);
                    let zValue = zValues[yValueIndex][xValueIndex];
                    zValue = Number.isNaN(zValue) ? null : zValue;
                    if (existedRowsDataIndex === -1) {
                        const z = [...rowDataTemplate.z];
                        z[index] = zValue;
                        rowsData.push({ ...rowDataTemplate, y: yValue, z });
                    }
                    else {
                        rowsData[existedRowsDataIndex].z[index] = zValue;
                    }
                });
            }
            else {
                rowsData.push(rowDataTemplate);
            }
        });
        rowsData.forEach((rowsDataItem) => {
            rows.push([
                `"${rowsDataItem.analysis}"`,
                `"${rowsDataItem.xConfiguration}"`,
                `"${rowsDataItem.yConfiguration}"`,
                `"${rowsDataItem.zConfiguration}"`,
                rowsDataItem.pending ? "Pending" : `"${rowsDataItem.x}"`,
                rowsDataItem.pending ? "Pending" : `"${rowsDataItem.y}"`,
                ...rowsDataItem.z.map((z) => (rowsDataItem.pending ? "Pending" : z === null ? "" : `"${z}"`)),
            ]);
        });
    });
    return convertToCSV([headers, ...rows]);
};
export const convertMetadataToCsv = (metadata, element, configurations) => {
    const parameters = metadata[0]?.data?.attributes?.map(({ name }) => name) || [];
    const headers = ["Scenario", "Configuration", ...parameters];
    const rows = [...configurations]
        .sort((config1, config2) => parseInt(config1.order) - parseInt(config2.order))
        .reduce((acc, { configurationId, analysisId }) => {
        if (configurationId !== null) {
            const configurationDescription = element.configurations.find((configuration) => configurationId === configuration.configurationId && configuration.analysisId === analysisId) || {};
            const { data: { data: results = [] }, } = metadata.find(({ recordId }) => recordId === configurationDescription.id) ||
                {};
            acc.push([
                configurationDescription.analysis,
                configurationDescription.configuration,
                ...results.flat().map((entry) => `"${entry}"`),
            ]);
        }
        return acc;
    }, []);
    const csvData = [headers, ...rows];
    return convertToCSV(csvData);
};
export const filterResultElementsWithLongCalculation = (currentState, payload) => {
    const { resultSetId, resultSetElementId, type } = payload;
    if (!!resultSetId && resultSetElementId == null && type === null) {
        return currentState.filter((el) => resultSetId !== el.resultSetId);
    }
    else {
        return currentState.filter((el) => !isEqual(el, payload));
    }
};
